import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Quote } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { RulingSource } from './RulingSource';

// AI drafts a citation paragraph for a real sala ruling; the lawyer edits it and inserts it into the case brief
export const CiteRuling = ({ matter, numero, fallo, testid, onUpdated }) => {
  const { api } = useAuth();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(false);
  const [src, setSrc] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const propose = async () => {
    setBusy('draft');
    try {
      const { data } = await api.post(`/crm/matters/${matter.id}/sala-profile/${numero}/cite/${fallo.i}`);
      const r = await runAiJob(api, null, null, { jobId: data.job_id, timeout: 120000 });
      setSrc(r.fallo || null); setConfirmed(false); setDraft(r.parrafo);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'No se pudo redactar la cita'); } finally { setBusy(null); }
  };
  const insert = async () => {
    setBusy('insert');
    try {
      await api.post(`/crm/matters/${matter.id}/brief/sala-rulings`, { numero, index: fallo.i, rol: fallo.rol, parrafo: draft });
      toast.success('Cita insertada en el escrito (sección «Jurisprudencia de la sala»)');
      setDone(true); setDraft(null); onUpdated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo insertar'); } finally { setBusy(null); }
  };
  if (done) return <p data-testid={`${testid}-done`} className="mt-1 text-[11px] text-emerald-300">Citado en el escrito.</p>;
  if (draft === null) return (
    <button type="button" data-testid={`${testid}-btn`} onClick={propose} disabled={!!busy} className="mt-1 flex items-center gap-1 text-[11px] text-amber-300 hover:underline disabled:opacity-50">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Quote className="h-3 w-3" />}{busy ? 'Redactando la cita...' : 'Citar en el escrito'}
    </button>
  );
  return (
    <div data-testid={`${testid}-editor`} className="mt-1 space-y-1">
      <div className="grid gap-2 md:grid-cols-2">
      <textarea data-testid={`${testid}-text`} rows={12} value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full rounded border border-slate-600 bg-slate-950 p-2 text-[11px] text-white" />
      {src && <RulingSource f={src} text={draft} testid={`${testid}-source`} />}
      </div>
      <label className="flex items-start gap-1.5 text-[11px] text-slate-200">
        <input type="checkbox" data-testid={`${testid}-confirm`} checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 accent-amber-500" />
        Confirmé cada dato de la cita con el fallo publicado.
      </label>
      <div className="flex gap-2">
        <button type="button" data-testid={`${testid}-insert`} onClick={insert} disabled={!!busy || draft.trim().length < 40 || !confirmed} className="flex items-center gap-1 rounded-full bg-amber-500 px-3 py-0.5 text-[11px] font-semibold text-slate-950 disabled:opacity-50">
          {busy === 'insert' && <Loader2 className="h-3 w-3 animate-spin" />}Insertar en el escrito
        </button>
        <button type="button" data-testid={`${testid}-discard`} onClick={() => setDraft(null)} className="rounded-full border border-slate-600 px-3 py-0.5 text-[11px] text-slate-300">Descartar</button>
      </div>
    </div>
  );
};
